import { Box, Chip, Tooltip } from '@mui/material';
import { ModelStatus } from '@pipelet/common';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

interface ModelStatusIndicatorProps {
  status: ModelStatus;
  size?: 'small' | 'medium';
  showLabel?: boolean;
  variant?: 'dot' | 'chip';
}

const STATUS_CONFIG: Record<ModelStatus, { color: string; label: string; description: string }> = {
  online: {
    color: '#4caf50',
    label: 'Online',
    description: 'Model is online and accepting requests',
  },
  starting: {
    color: '#4caf50',
    label: 'Starting',
    description: 'Model is starting up',
  },
  standby: {
    color: '#bebebe',
    label: 'Standby',
    description: 'Model is on standby',
  },
  maintenance: {
    color: '#2196f3',
    label: 'Maintenance',
    description: 'Model is under maintenance',
  },
  offline: {
    color: '#f0f0f0',
    label: 'Offline',
    description: 'Model is offline',
  },
  unknown: {
    color: '#757575',
    label: 'Unknown',
    description: 'Model status is unknown',
  },
};

export function ModelStatusIndicator({
  status,
  size = 'medium',
  showLabel = false,
  variant = 'dot',
}: ModelStatusIndicatorProps): JSX.Element {
  const config = STATUS_CONFIG[status];
  const dotSize = size === 'small' ? 8 : 12;

  if (variant === 'chip') {
    return (
      <Tooltip title={config.description} arrow>
        <Chip
          icon={
            <FiberManualRecordIcon
              sx={{
                fontSize: 12,
                color: config.color,
                ...(status === 'starting' && {
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.3 },
                  },
                }),
              }}
            />
          }
          label={config.label}
          size={size}
          variant="outlined"
          sx={{
            borderColor: config.color,
            color: config.color,
            '& .MuiChip-icon': {
              marginLeft: '8px',
            },
          }}
        />
      </Tooltip>
    );
  }

  // Dot variant
  return (
    <Tooltip title={`${config.label}: ${config.description}`} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <FiberManualRecordIcon
          sx={{
            fontSize: dotSize,
            color: config.color,
            ...(status === 'starting' && {
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }),
          }}
        />
        {showLabel && (
          <Box
            component="span"
            sx={{
              fontSize: size === 'small' ? '0.75rem' : '0.875rem',
              color: 'text.secondary',
            }}
          >
            {config.label}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
